import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DynamicTable from "../lab-dynamic-table";
import axios from "axios";
import { toast } from "react-toastify";
import { FileDownload, Print } from "@mui/icons-material";
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DateRangeSelector from "../DateRangeSelector";
import { formatCurrency } from "../../utils/numberUtils";

// Reusable button styles
const gradientButtonStyle = {
  minWidth: { xs: "auto", md: "80px" },
  fontSize: { xs: "12px", md: "12px" },
  fontWeight: "600",
  textTransform: "none",
  borderRadius: "8px",
  background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
  color: "#ffffff",
  boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
    boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
    transform: "translateY(-1px)",
  },
};

export const ProcDepWiseSummaryTab = () => {
  const nowDate = new Date().toISOString().split("T")[0];
  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(nowDate);
  const [toDate, setToDate] = useState(nowDate);
  const [summarizeBy, setSummarizeBy] = useState("procedure");
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(100);
  
  // Filter states
  const [departments, setDepartments] = useState<any[]>([]);
  const [procedures, setProcedures] = useState<any[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<any>(null);
  const [selectedProcedure, setSelectedProcedure] = useState<any>(null);

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
  };

  // Fetch departments
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/acc-department/getAllByPagination`,
        {
          params: { pageNo: 1, count: 1000 },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        setDepartments(response.data.data || []);
      }
    } catch (err: any) {
      console.error("Error fetching departments:", err);
    }
  };

  // Fetch procedures (categories)
  const fetchProcedures = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/income-category/getAllCategories`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        setProcedures(response.data.data || []);
      }
    } catch (err: any) {
      console.error("Error fetching procedures:", err);
    }
  };

  const fetchSummaryData = async (): Promise<void> => {
    setLoading(true);
    try {
      const params: any = {
        fromDate,
        toDate,
        summarizeBy,
      };

      // Add filter parameters based on selection
      if (summarizeBy === "department" && selectedDepartment) {
        params.departmentId = selectedDepartment._id;
      } else if (summarizeBy === "procedure" && selectedProcedure) {
        params.categoryId = selectedProcedure._id;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/income/getDepartmentWiseIncomeSummary`,
        {
          params,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (response.data.isSuccess) {
        setSummaryData(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch summary data");
      }
    } catch (err: any) {
      toast.error(err.message || "Error fetching summary data");
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch departments and procedures on mount
  useEffect(() => {
    fetchDepartments();
    fetchProcedures();
  }, []);

  useEffect(() => {
    fetchSummaryData();
  }, [fromDate, toDate, summarizeBy, selectedDepartment, selectedProcedure]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setCount(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Export summary data to Excel file
  const handleExcelExport = () => {
    if (!summaryData.length) return toast.warn("No data to export.");

    // Map summary data to worksheet format based on selected summarization type
    const worksheetData = summaryData.map(item => {
      const baseData: any = {};

      // Add the primary column based on summarization type
      if (summarizeBy === "procedure") {
        baseData["Procedure"] = item.procedure || "--";
      } else if (summarizeBy === "department") {
        baseData["Department"] = item.department || "--";
      }

      baseData["Total Amount"] = item.totalAmount || 0;
      baseData["Total Discount"] = item.totalDiscount || 0;
      baseData["Total Net Amount"] = item.totalNetAmount || 0;
      baseData["Total Paid Amount"] = item.totalPayment || 0;
      baseData["Item Count"] = item.itemCount || 0;

      return baseData;
    });

    const worksheet = xlsx.utils.json_to_sheet(worksheetData);
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "Summary");
    xlsx.writeFile(workbook, `ProcDepWiseSummary_${fromDate}_to_${toDate}.xlsx`);
  };

  const handlePrint = () => {
    if (!summaryData.length) return toast.warn("No data to print.");

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    let yPosition = margin;

    // Professional header with minimal, clean design
    // Dynamically set report title based on summarization type
    const reportTitle = summarizeBy === "procedure" 
      ? "PROCEDURE WISE SUMMARY REPORT" 
      : "DEPARTMENT WISE SUMMARY REPORT";
    doc
      .setFontSize(10)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text(reportTitle, pageWidth / 2, yPosition, {
        align: "center",
      });
    
    yPosition += 6;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    // Subtle metadata section with smaller font
    yPosition += 3;
    doc
      .setFontSize(6.5)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Report Period: ${fromDate} to ${toDate} | Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 6;

    // Define table headers based on summarization type
    const head = [
      summarizeBy === "procedure"
        ? ["PROCEDURE", "TOTAL AMOUNT", "TOTAL DISCOUNT", "NET AMOUNT", "PAID AMOUNT", "ITEM COUNT"]
        : ["DEPARTMENT", "TOTAL AMOUNT", "TOTAL DISCOUNT", "NET AMOUNT", "PAID AMOUNT", "ITEM COUNT"],
    ];

    // Map summary data to table rows based on summarization type
    const body = summaryData.map(item =>
      summarizeBy === "procedure"
        ? [
            item.procedure || "--",
            formatCurrency(item.totalAmount || 0),
            formatCurrency(item.totalDiscount || 0),
            formatCurrency(item.totalNetAmount || 0),
            formatCurrency(item.totalPayment || 0),
            item.itemCount || 0,
          ]
        : [
            item.department || "--",
            formatCurrency(item.totalAmount || 0),
            formatCurrency(item.totalDiscount || 0),
            formatCurrency(item.totalNetAmount || 0),
            formatCurrency(item.totalPayment || 0),
            item.itemCount || 0,
          ]
    );

    // Calculate totals
    const totalAmount = summaryData.reduce((sum, item) => sum + (Number(item?.totalAmount) || 0), 0);
    const totalDiscount = summaryData.reduce((sum, item) => sum + (Number(item?.totalDiscount) || 0), 0);
    const totalNetAmount = summaryData.reduce((sum, item) => sum + (Number(item?.totalNetAmount) || 0), 0);
    const totalPayment = summaryData.reduce((sum, item) => sum + (Number(item?.totalPayment) || 0), 0);
    const totalItemCount = summaryData.reduce((sum, item) => sum + (Number(item?.itemCount) || 0), 0);

    body.push([
      "TOTAL",
      formatCurrency(totalAmount),
      formatCurrency(totalDiscount),
      formatCurrency(totalNetAmount),
      formatCurrency(totalPayment),
      totalItemCount,
    ]);

    // Clean, professional table styling
    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: { 
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 6,
        halign: 'center',
        valign: 'middle',
        cellPadding: 2
      },
      bodyStyles: {
        fontSize: 5.5,
        textColor: [40, 40, 40],
        cellPadding: 1.8
      },
      columnStyles: {
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didParseCell: function(data) {
        // Professional total row with premium styling
        if (data.row.index === body.length - 1) {
          data.cell.styles.fillColor = [25, 55, 85];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 5.8;
          data.cell.styles.lineColor = [200, 200, 200];
          data.cell.styles.lineWidth = 0.3;
          (data.cell.styles as any).cellPadding = { top: 2.5, right: 2, bottom: 2.5, left: 2 };
        }
      },
      didDrawPage: function(data) {
        // Add subtle footer with page number
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        const pageNum = data.pageNumber;
        
        doc.setFontSize(5);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${pageNum} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      }
    });

    doc.autoPrint();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);
    
    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }
      window.removeEventListener("focus", cleanup);
    };
    
    window.addEventListener("focus", cleanup);
    iframe.src = blobUrl;
    
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Error triggering print dialog:", e);
        cleanup();
      }
    };
    
    // Fallback print trigger
    setTimeout(() => {
      try {
        const win = iframe.contentWindow;
        if (win && document.body.contains(iframe)) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Fallback print attempt failed:", e);
        cleanup();
      }
    }, 1000);
  };

  // Define table headers for the DynamicTable component based on summarization type
  const tableHeaders = summarizeBy === "procedure"
    ? ["Procedure", "Total Amount", "Total Discount", "Net Amount", "Paid Amount", "Item Count"]
    : ["Department", "Total Amount", "Total Discount", "Net Amount", "Paid Amount", "Item Count"];

  // Map summary data to table rows for display in DynamicTable component
  const tableData = summaryData.map(item =>
    summarizeBy === "procedure"
      ? [
          item.procedure || "--",
          formatCurrency(item.totalAmount || 0),
          formatCurrency(item.totalDiscount || 0),
          formatCurrency(item.totalNetAmount || 0),
          formatCurrency(item.totalPayment || 0),
          item.itemCount || 0,
        ]
      : [
          item.department || "--",
          formatCurrency(item.totalAmount || 0),
          formatCurrency(item.totalDiscount || 0),
          formatCurrency(item.totalNetAmount || 0),
          formatCurrency(item.totalPayment || 0),
          item.itemCount || 0,
        ]
  );

  const totalPaid = summaryData.reduce(
    (sum, item) => sum + (item.totalPayment || 0),
    0
  );

  return (
    <Box>
      <Box
        display="flex"
        sx={{ background: "#ffffff", borderRadius: "8px" }}
        justifyContent="space-between"
        alignItems="center"
        paddingY={1}
        paddingX={1}
        flexWrap="wrap"
        gap={2}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            alignSelf: "flex-end",
          }}
        >
          <Tooltip title="Export data to Excel file" arrow placement="top">
            <Button
              variant="contained"
              size="small"
              startIcon={<FileDownload />}
              onClick={handleExcelExport}
              sx={gradientButtonStyle}
            >
              Excel
            </Button>
          </Tooltip>
          <Tooltip title="Print summary report" arrow placement="top">
            <Button
              variant="contained"
              size="small"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={gradientButtonStyle}
            >
              Print
            </Button>
          </Tooltip>
        </Box>

        <Box
          display="flex"
          gap={1.5}
          alignItems="flex-end"
          flexWrap="wrap"
          justifyContent={{ xs: "flex-start", md: "flex-end" }}
          flexGrow={1}
        >
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Summarize By
            </Typography>
            <Select
              size="small"
              value={summarizeBy}
              onChange={e => {
                setSummarizeBy(e.target.value);
                // Clear filters when summarize by changes
                setSelectedDepartment(null);
                setSelectedProcedure(null);
              }}
              sx={{ minWidth: { xs: "120px", md: "150px" } }}
              className="custom-input"
            >
              <MenuItem value="procedure">Procedure</MenuItem>
              <MenuItem value="department">Department</MenuItem>
            </Select>
          </Box>
          
          {/* Conditional Procedure Filter */}
          {summarizeBy === "procedure" && (
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
                Procedure
              </Typography>
              <Autocomplete
                size="small"
                value={selectedProcedure}
                onChange={(event, newValue) => {
                  setSelectedProcedure(newValue);
                }}
                options={procedures}
                getOptionLabel={option => option?.categoryName || ""}
                renderInput={params => (
                  <TextField
                    {...params}
                    className="custom-input"
                    placeholder="Select Procedure"
                    sx={{ minWidth: "200px" }}
                  />
                )}
                clearOnEscape
              />
            </Box>
          )}

          {/* Conditional Department Filter */}
          {summarizeBy === "department" && ( 
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
                Department
              </Typography>
              <Autocomplete
                size="small"
                value={selectedDepartment}
                onChange={(event, newValue) => {
                  setSelectedDepartment(newValue);
                }}
                options={departments}
                getOptionLabel={option => option?.department || ""}
                renderInput={params => (
                  <TextField
                    {...params}
                    className="custom-input"
                    placeholder="Select Department"
                    sx={{ minWidth: "200px" }}
                  />
                )}
                clearOnEscape
              />
            </Box>
          )}
          <DateRangeSelector
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={handleDateRangeChange}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>

      <Box paddingX={1} sx={{ marginTop: "16px" }}>
        <DynamicTable
          headers={tableHeaders}
          loading={loading}
          data={tableData}
          totalAmount={totalPaid}
          height={"60vh"}
          page={page}
          rowsPerPage={count}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          totalRows={summaryData.length}
        />
      </Box>
    </Box>
  );
};
